/**
 * @fileoverview Pure helpers behind the "Titles by appointment type" preset
 * editor (MOR-2354): the system default a job would derive, the effective
 * title once a type-scoped override is considered, and a per-type reset.
 * Kept out of the component file so Fast Refresh sees only components there.
 */

import { APPOINTMENT_TYPE_TO_ADMIN_TYPE } from "~/convex/jobs/precedents/fieldMapping";
import {
  scopedTitleKey,
  TITLE_KEYS,
  type TitleKey,
} from "~/convex/lib/titleScopes";

export const TITLE_LABELS: Record<TitleKey, string> = {
  signatory_title: "Signatory title",
  practitioner_title: "Practitioner title",
};

/** The system default for a title on an appointment type. */
export function systemTitleDefault(appointmentType: string): string {
  return APPOINTMENT_TYPE_TO_ADMIN_TYPE[appointmentType] ?? "";
}

/** The title a job of this type will carry: the override, else the default. */
export function effectiveTitle(
  values: Readonly<Record<string, string>>,
  titleKey: TitleKey,
  appointmentType: string,
): { value: string; overridden: boolean } {
  const override = values[scopedTitleKey(titleKey, appointmentType)]?.trim();
  return override
    ? { value: override, overridden: true }
    : { value: systemTitleDefault(appointmentType), overridden: false };
}

/** Values with this type's overrides cleared (reset to default). */
export function withTypeReset(
  values: Readonly<Record<string, string>>,
  appointmentType: string,
): Record<string, string> {
  const drop = new Set(
    TITLE_KEYS.map((titleKey) => scopedTitleKey(titleKey, appointmentType)),
  );
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !drop.has(key)),
  );
}
